import React, { useState, useEffect } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import axios from 'axios';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { Container } from '@material-ui/core';

import { Loading } from '../stories/Loading';

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

const SummaryTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 20,
    border: 2,
    borderColor: theme.palette.common.black,
    borderStyle: 'solid',
  },
}))(TableCell);

const useStyles = makeStyles((theme) => ({
  root: {
    width: 'auto',
    textAlign: 'start',
    margin: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    textAlign: 'start',
    height: 'calc(100VH - 120px)',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100VW',
  },
  componentTitle: {
    color: '#142966',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: '24px',
    marginBottom: '0px',
  },
  separatorLine: {
    height: '2px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
    background: '#004878',
    border: 'none',
  },
  table: {
    minWidth: 650,
  },
}));

interface RoomAvailableDetailsProps {}

const RoomAvailableDetails = (props: RoomAvailableDetailsProps) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [roomAvailability, setRoomAvailability] = useState<any>();
  const [summary, setSummary] = useState<any>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const response = await axios.get('/api/getRoomVacantDetails');
        const getRoomVacantDetails = response.data;
        setRoomAvailability(getRoomVacantDetails);
        const roomSummary = Object.keys(getRoomVacantDetails).reduce(
          (acc, roomType) => {
            acc.occupied += getRoomVacantDetails[roomType]['non_vacant'];
            acc.vacant += getRoomVacantDetails[roomType]['vacant'];
            return acc;
          },
          { vacant: 0, occupied: 0 }
        );
        setSummary(roomSummary);
        setError(undefined);
      } catch (error: any) {
        setError(error.message);
        setSummary(undefined);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleGetVacantReport = () => {
    axios
      .get(`/api/getVacantList`, { responseType: 'blob' })
      .then((res) => res.data)
      .then((blob) => {
        var file = new Blob([blob], { type: 'application/pdf' });
        var url = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `VacantList.pdf`);
        document.body.appendChild(link);
        link.click();
        link?.parentNode?.removeChild(link);
      })
      .catch((err) => {
        alert('Error while downloading VacantList report!');
        console.log(err);
      })
      .finally(() => setLoading(false));
  };

  const handleGetOccupiedReport = () => {
    axios
      .get(`/api/getOccupiedList`, { responseType: 'blob' })
      .then((res) => res.data)
      .then((blob) => {
        var file = new Blob([blob], { type: 'application/pdf' });
        var url = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `OccupiedList.pdf`);
        document.body.appendChild(link);
        link.click();
        link?.parentNode?.removeChild(link);
      })
      .catch((err) => {
        alert('Error while downloading VacantList report!');
        console.log(err);
      })
      .finally(() => setLoading(false));
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    );
  } else if (error) {
    return <div>{error}</div>;
  } else {
    return (
      <div className={classes.root}>
        <h2 className={classes.componentTitle}>Room Availability</h2>
        <hr className={classes.separatorLine} />
        <Container maxWidth="md" style={{ marginTop: '24px' }}>
          <TableContainer component={Paper}>
            <Table className={classes.table} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Room Type</TableCell>
                  <TableCell>Vacant</TableCell>
                  <TableCell>Occupied</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roomAvailability &&
                  Object.keys(roomAvailability).map((roomType) => (
                    <StyledTableRow key={roomType}>
                      <StyledTableCell component="th" scope="row">
                        {roomType}
                      </StyledTableCell>
                      <StyledTableCell>
                        {roomAvailability[roomType]['vacant']}{' '}
                      </StyledTableCell>
                      <StyledTableCell>
                        {roomAvailability[roomType]['non_vacant']}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                <StyledTableRow key="Total">
                  <SummaryTableCell>Total</SummaryTableCell>
                  <SummaryTableCell>
                    <p
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>{summary?.vacant} </span>
                      <span
                        style={{
                          fontSize: '18px',
                          marginLeft: '-64px',
                          color: 'blue',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                        onClick={handleGetVacantReport}
                      >
                        Get Details
                      </span>
                    </p>
                  </SummaryTableCell>
                  <SummaryTableCell>
                    <p
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span> {summary?.occupied} </span>
                      <span
                        style={{
                          fontSize: '18px',
                          marginLeft: '-64px',
                          color: 'blue',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                        onClick={handleGetOccupiedReport}
                      >
                        Get Details
                      </span>
                    </p>
                  </SummaryTableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </div>
    );
  }
};

export default RoomAvailableDetails;
